import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { User, Check, Pencil, Plus, Trash2, ShieldCheck, Mail, Link2, Download } from "lucide-react";
import AddCoachDialog from "@/components/AddCoachDialog";
import HummingbirdBadgeCard from "@/components/HummingbirdBadgeCard";
import InviteLinkDialog from "@/components/InviteLinkDialog";
import AccessRightsPanel from "@/components/AccessRightsPanel";

export default function CoachesPage({ mode = "coaches" }) {
  const isLecturersMode = mode === "lecturers";
  const isStaffMode = mode === "staff";
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isMaster = !!user?.is_master_admin;
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [inviteFor, setInviteFor] = useState(null);
  const [permMeta, setPermMeta] = useState(null);
  const [rightsOpen, setRightsOpen] = useState(null);
  const canGrant = isAdmin || isMaster;

  useEffect(() => {
    if (!canGrant) return;
    api.get("/permissions/meta").then((r) => setPermMeta(r.data)).catch(() => {});
  }, [canGrant]);

  const toggleExternal = async (c) => {
    try {
      const { data } = await api.put(`/users/${c.user_id}/external`, { is_external: !c.is_external });
      setCoaches((arr) => arr.map((x) => (x.user_id === c.user_id ? { ...x, is_external: data.is_external } : x)));
      toast.success(data.is_external ? "Als externer Coach markiert" : "Markierung „extern“ entfernt");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const onRightsSaved = (c, data) => {
    setCoaches((arr) => arr.map((x) => (x.user_id === c.user_id ? { ...x, ...data } : x)));
    setRightsOpen(null);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coaches");
      // In lecturers mode, only show role === "lecturer".
      // In coaches mode, hide lecturers (they belong to the Dozenten section).
      const filtered = isLecturersMode
        ? data.filter((c) => c.role === "lecturer")
        : isStaffMode
          ? data.filter((c) => ["admin", "manager"].includes(c.role) || (c.extra_roles || []).some((r) => ["admin", "manager"].includes(r)))
          : data.filter((c) => c.role !== "lecturer" && (c.role === "coach" || (c.extra_roles || []).includes("coach")));
      setCoaches(filtered);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const canEdit = (c) => isAdmin || user?.user_id === c.user_id;
  const canDelete = (c) => isAdmin && c.role !== "admin" && c.user_id !== user?.user_id;

  const handleSave = async (c) => {
    const e = edits[c.user_id];
    if (!e) return;
    try {
      const { data } = await api.put(`/coaches/${c.user_id}`, e);
      setCoaches((arr) => arr.map((x) => (x.user_id === c.user_id ? { ...x, ...data } : x)));
      setEdits((m) => {
        const cp = { ...m };
        delete cp[c.user_id];
        return cp;
      });
      toast.success("Coach aktualisiert");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    }
  };

  const startEdit = (c) => {
    setEdits((m) => ({
      ...m,
      [c.user_id]: {
        name: c.name,
        email: c.email,
        weekly_hours_target: c.weekly_hours_target,
        meet_link: c.meet_link || "",
        role: c.role,
        extra_roles: c.extra_roles || [],
      },
    }));
  };

  const toggleExtraRole = (user_id, role) => {
    setEdits((m) => {
      const cur = m[user_id] || {};
      const list = cur.extra_roles || [];
      const next = list.includes(role) ? list.filter((r) => r !== role) : [...list, role];
      return { ...m, [user_id]: { ...cur, extra_roles: next } };
    });
  };

  const toggleApproveRight = async (c) => {
    if (!isAdmin) return;
    const next = !c.can_approve_vacations;
    try {
      await api.put(`/users/${c.user_id}/approve-permission`, { can_approve_vacations: next });
      setCoaches((arr) => arr.map((x) => (x.user_id === c.user_id ? { ...x, can_approve_vacations: next } : x)));
      toast.success(next ? "Genehmigungsrecht erteilt" : "Genehmigungsrecht entzogen");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const toggleContractRight = async (c) => {
    if (!isAdmin) return;
    const next = !c.can_edit_contracts;
    try {
      await api.put(`/users/${c.user_id}/contract-permission`, { can_edit_contracts: next });
      setCoaches((arr) => arr.map((x) => (x.user_id === c.user_id ? { ...x, can_edit_contracts: next } : x)));
      toast.success(next ? "Vertragsrecht erteilt" : "Vertragsrecht entzogen");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleDelete = async (c) => {    if (
      !window.confirm(
        `Coach "${c.name}" wirklich löschen?\n\nAlle Einträge dieses Coaches werden ebenfalls dauerhaft entfernt.`
      )
    )
      return;
    try {
      await api.delete(`/coaches/${c.user_id}`);
      setCoaches((arr) => arr.filter((x) => x.user_id !== c.user_id));
      toast.success(`${c.name} gelöscht`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleReinvite = async (c) => {
    setInviteFor(c);
  };

  const downloadCoachReport = async (c) => {
    try {
      const res = await api.get(`/coaches/${c.user_id}/report.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `coach_bericht_${(c.name || "coach").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1400px] mx-auto" data-testid={isLecturersMode ? "lecturers-root" : isStaffMode ? "staff-root" : "coaches-root"}>
      <div className="mb-10 border-b border-slate-200 pb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">
            {isAdmin ? "Team-Verwaltung" : "Mein Profil"}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">
            {isAdmin ? (isLecturersMode ? "Dozenten" : isStaffMode ? "Mitarbeiter" : "Coaches") : user?.name}
          </h1>
          <p className="text-sm text-slate-500 mt-3 max-w-2xl">
            {isAdmin
              ? (isLecturersMode
                  ? "Verwalte die Dozentinnen und Dozenten der Weiterbildungen. Dozenten haben Zugriff auf zugewiesene Klassen (Anwesenheit, Klassenbuch, Frühwarnung) — nicht auf AVGS-Kunden."
                  : isStaffMode ? "Interne Mitarbeiter aus Backoffice und Management – Rollen, Rechte und Wochenstunden verwalten."
                  : "Lege neue Coaches an, lösche bestehende oder passe Wochenstunden-Ziele an. Coaches sehen nur ihre eigenen Daten.")
              : "Hier kannst du deinen Namen und dein Wochenstunden-Ziel anpassen."}
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid={isLecturersMode ? "add-lecturer-button" : isStaffMode ? "add-staff-button" : "add-coach-button"}
            onClick={() => setAddOpen(true)}
            className="bg-slate-900 text-white px-6 py-3 text-sm font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
          >
            <Plus size={16} strokeWidth={1.5} />
            {isLecturersMode ? "Dozent/-in hinzufügen" : isStaffMode ? "Mitarbeiter hinzufügen" : "Coach hinzufügen"}
          </button>
        )}
      </div>

      {!isAdmin && <HummingbirdBadgeCard />}

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full" data-testid={isLecturersMode ? "lecturers-table" : "coaches-table"}>
          <thead>
            <tr className="border-b border-slate-200">
              {[isLecturersMode ? "Dozent/-in" : isStaffMode ? "Mitarbeiter" : "Coach", "E-Mail", "Rolle", "Wochenstunden", "Aktionen"].map((h) => (
                <th
                  key={h}
                  className="text-left text-xs font-bold uppercase tracking-overline text-slate-500 px-6 py-4"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="p-10 text-center text-sm text-slate-500">Lädt…</td>
              </tr>
            )}
            {!loading &&
              coaches.map((c) => {
                const editing = edits[c.user_id];
                return (
                  <tr
                    key={c.user_id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`coach-row-${c.user_id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                          {c.role === "admin" ? (
                            <ShieldCheck size={14} strokeWidth={1.5} className="text-slate-700" />
                          ) : c.picture ? (
                            <img src={c.picture} alt={c.name} className="w-full h-full object-cover" />
                          ) : (
                            <User size={14} strokeWidth={1.5} className="text-slate-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          {editing ? (
                            <input
                              data-testid={`coach-name-input-${c.user_id}`}
                              className="input-tech text-sm mb-2"
                              value={editing.name}
                              onChange={(ev) =>
                                setEdits((m) => ({ ...m, [c.user_id]: { ...editing, name: ev.target.value } }))
                              }
                            />
                          ) : (
                            <div className="text-sm font-medium text-slate-900 mb-0.5">{c.name}</div>
                          )}
                          {editing ? (
                            <input
                              data-testid={`coach-meet-input-${c.user_id}`}
                              type="url"
                              placeholder="Persönlicher Meet- / Buchungslink"
                              className="input-tech text-xs font-mono"
                              value={editing.meet_link || ""}
                              onChange={(ev) =>
                                setEdits((m) => ({ ...m, [c.user_id]: { ...editing, meet_link: ev.target.value } }))
                              }
                            />
                          ) : c.meet_link ? (
                            <a
                              data-testid={`coach-meet-link-${c.user_id}`}
                              href={c.meet_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-[#0a788c] hover:text-[#0a96b4] font-mono break-all block"
                              title="Meet-Link öffnen"
                            >
                              🔗 {c.meet_link.replace(/^https?:\/\//, "")}
                            </a>
                          ) : (
                            <div className="text-[10px] text-slate-400 italic">Kein Meet-Link hinterlegt</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-mono">
                      {editing && isAdmin ? (
                        <input
                          data-testid={`coach-email-input-${c.user_id}`}
                          type="email"
                          className="input-tech text-sm font-mono"
                          value={editing.email}
                          onChange={(ev) =>
                            setEdits((m) => ({ ...m, [c.user_id]: { ...editing, email: ev.target.value } }))
                          }
                        />
                      ) : (
                        c.email
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      <div className="flex items-center gap-2 flex-wrap">
                        {editing && isAdmin ? (
                          <div className="flex flex-col gap-1">
                            <select
                              data-testid={`coach-role-select-${c.user_id}`}
                              className="input-tech text-xs py-1 max-w-[120px]"
                              value={editing.role || c.role}
                              onChange={(ev) =>
                                setEdits((m) => ({ ...m, [c.user_id]: { ...editing, role: ev.target.value } }))
                              }
                            >
                              <option value="coach">Coach</option>
                              <option value="lecturer">Dozent</option>
                              {(editing.role || c.role) === "manager" && <option value="manager">Manager (alt)</option>}
                              <option value="admin">Admin</option>
                            </select>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {["coach", "lecturer", "admin"].filter((r) => r !== (editing.role || c.role)).map((r) => (
                                <label key={r} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-slate-200 hover:bg-slate-50 cursor-pointer">
                                  <input type="checkbox"
                                    checked={(editing.extra_roles || []).includes(r)}
                                    onChange={() => toggleExtraRole(c.user_id, r)}
                                    className="accent-[#00CCC9] w-3 h-3"
                                    data-testid={`coach-extra-${c.user_id}-${r}`} />
                                  + {r}
                                </label>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            <span
                              className={`inline-block px-2 py-1 font-bold uppercase tracking-overline text-[10px] ${
                                c.role === "admin"
                                  ? "bg-slate-900 text-white"
                                  : c.role === "manager"
                                  ? "bg-[#0a788c] text-white"
                                  : "bg-slate-100 text-slate-700"
                              }`}
                            >
                              {c.role}
                            </span>
                            {(c.extra_roles || []).map((r) => (
                              <span key={r} className="inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-overline bg-[#00CCC9]/20 text-[#005E5C] border border-[#00CCC9]/40">
                                + {r === "manager" ? "alle Kunden" : r}
                              </span>
                            ))}
                            {(c.can_approve_vacations || c.is_master_admin) && (
                              <span
                                data-testid={`coach-approver-badge-${c.user_id}`}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-overline bg-amber-50 text-amber-800 border border-amber-200"
                                title="Darf Urlaubsanträge genehmigen"
                              >
                                ✓ Urlaub-Genehmiger
                              </span>
                            )}
                            {isAdmin && !c.is_master_admin && (
                              <button
                                data-testid={`coach-approver-toggle-${c.user_id}`}
                                onClick={() => toggleApproveRight(c)}
                                className={`text-[10px] font-bold uppercase tracking-overline px-2 py-1 border transition-colors ${
                                  c.can_approve_vacations
                                    ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                                    : "border-slate-300 text-slate-500 hover:bg-slate-50"
                                }`}
                                title="Klicken um Urlaubs-Genehmigungsrecht umzuschalten"
                              >
                                {c.can_approve_vacations ? "Urlaub-Recht entziehen" : "+ Genehmiger"}
                              </button>
                            )}
                            {(c.can_edit_contracts || c.role === "admin" || c.role === "manager") && (
                              <span
                                data-testid={`coach-contract-badge-${c.user_id}`}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-overline bg-teal-50 text-teal-800 border border-teal-200"
                                title="Darf Verträge bearbeiten (freigeben, ablehnen, Link erneut senden)"
                              >
                                ✓ Vertrags-Recht
                              </span>
                            )}
                            {isAdmin && c.role !== "admin" && c.role !== "manager" && (
                              <button
                                data-testid={`coach-contract-toggle-${c.user_id}`}
                                onClick={() => toggleContractRight(c)}
                                className={`text-[10px] font-bold uppercase tracking-overline px-2 py-1 border transition-colors ${
                                  c.can_edit_contracts
                                    ? "border-teal-300 text-teal-700 hover:bg-teal-50"
                                    : "border-slate-300 text-slate-500 hover:bg-slate-50"
                                }`}
                                title="Klicken um das Recht 'Verträge bearbeiten' umzuschalten"
                              >
                                {c.can_edit_contracts ? "Vertragsrecht entziehen" : "+ Vertragsrecht"}
                              </button>
                            )}
                            {canGrant && (
                              <>
                                <label className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-overline text-slate-600 cursor-pointer" title="Externe/Freelancer dürfen stattgefundene oder unterschriebene Termine nicht mehr ändern">
                                  <input type="checkbox" checked={!!c.is_external} onChange={() => toggleExternal(c)} data-testid={`coach-external-${c.user_id}`} /> Extern
                                </label>
                                {!(c.all_roles || []).includes("admin") && <button
                                  onClick={() => setRightsOpen(rightsOpen === c.user_id ? null : c.user_id)}
                                  className="text-[10px] font-bold uppercase tracking-overline px-2 py-1 border border-slate-300 text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors"
                                  data-testid={`coach-rights-toggle-${c.user_id}`}
                                >
                                  {rightsOpen === c.user_id ? "Schließen" : "Zugänge"}
                                </button>}
                              </>
                            )}
                          </div>
                        )}
                        {canGrant && rightsOpen === c.user_id && permMeta && (
                          <AccessRightsPanel person={c} groups={permMeta.groups} onSaved={(d) => onRightsSaved(c, d)} />
                        )}
                        {c.invitation_pending && (
                          <span
                            data-testid={`coach-pending-${c.user_id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 font-bold uppercase tracking-overline bg-amber-50 text-amber-700 border border-amber-200"
                          >
                            <Mail size={10} strokeWidth={1.5} />
                            Eingeladen
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-sm text-slate-900">
                      {editing ? (
                        <input
                          data-testid={`coach-target-input-${c.user_id}`}
                          type="number"
                          min="0"
                          className="input-tech max-w-[100px]"
                          value={editing.weekly_hours_target}
                          onChange={(ev) =>
                            setEdits((m) => ({
                              ...m,
                              [c.user_id]: {
                                ...editing,
                                weekly_hours_target: parseInt(ev.target.value, 10) || 0,
                              },
                            }))
                          }
                        />
                      ) : (
                        <>{c.weekly_hours_target ?? 0}h / Woche</>
                      )}
                      {isAdmin && (
                        <div className="mt-1 flex items-center gap-1" data-testid={`coach-vac-quota-${c.user_id}`}>
                          <input
                            type="number"
                            min="0"
                            max="366"
                            defaultValue={c.vacation_days_year ?? 30}
                            onBlur={async (ev) => {
                              const v = parseInt(ev.target.value, 10);
                              if (isNaN(v) || v === (c.vacation_days_year ?? 30)) return;
                              try {
                                await api.put(`/users/${c.user_id}/vacation-quota`, { vacation_days_year: v });
                                setCoaches((arr) => arr.map((x) => (x.user_id === c.user_id ? { ...x, vacation_days_year: v } : x)));
                                toast.success("Urlaubs-Kontingent gespeichert");
                              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
                            }}
                            className="input-tech max-w-[70px] text-xs py-1"
                          />
                          <span className="text-[10px] text-slate-500">Tage Urlaub/Jahr</span>
                        </div>
                      )}
                      {isAdmin && (
                        <div className="mt-1" data-testid={`coach-waat-role-${c.user_id}`}>
                          <select
                            className="input-tech text-xs py-1 max-w-[220px]"
                            value={c.waat_role || ""}
                            onChange={async (ev) => {
                              const v = ev.target.value || null;
                              try {
                                await api.put(`/kpi/users/${c.user_id}/role`, { waat_role: v });
                                setCoaches((arr) => arr.map((x) => (x.user_id === c.user_id ? { ...x, waat_role: v } : x)));
                                toast.success(v ? "WAAT-Rolle gespeichert" : "Rolle entfernt");
                              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
                            }}
                          >
                            <option value="">— WAAT-Rolle wählen —</option>
                            <option value="geschaeftsfuehrung">Geschäftsführung</option>
                            <option value="backoffice">Backoffice / Assistenz</option>
                            <option value="sales_wb">Bildungsmanager (Sales WB)</option>
                            <option value="sales_avgs">Sales AVGS & Jobcoaching</option>
                          </select>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2 justify-end">
                        {canEdit(c) && (
                          editing ? (
                            <>
                              <button
                                data-testid={`coach-save-${c.user_id}`}
                                onClick={() => handleSave(c)}
                                className="px-3 py-2 border border-slate-900 bg-slate-900 text-white text-xs hover:bg-slate-800 flex items-center gap-1"
                              >
                                <Check size={12} strokeWidth={2} /> Speichern
                              </button>
                              <button
                                onClick={() =>
                                  setEdits((m) => {
                                    const cp = { ...m };
                                    delete cp[c.user_id];
                                    return cp;
                                  })
                                }
                                className="px-3 py-2 border border-slate-300 text-xs text-slate-700 hover:bg-slate-100"
                              >
                                Abbrechen
                              </button>
                            </>
                          ) : (
                            <button
                              data-testid={`coach-edit-${c.user_id}`}
                              onClick={() => startEdit(c)}
                              className="px-3 py-2 border border-slate-200 text-xs text-slate-700 hover:bg-slate-100 flex items-center gap-1"
                            >
                              <Pencil size={12} strokeWidth={1.5} /> Bearbeiten
                            </button>
                          )
                        )}
                        {isAdmin && c.role !== "admin" && !editing && (
                          <button
                            data-testid={`coach-report-${c.user_id}`}
                            onClick={() => downloadCoachReport(c)}
                            className="px-3 py-2 border border-slate-200 text-xs text-slate-700 hover:bg-slate-900 hover:text-white flex items-center gap-1"
                            title="Coach-Bericht als PDF"
                          >
                            <Download size={12} strokeWidth={1.5} /> Bericht
                          </button>
                        )}
                        {isAdmin && c.invitation_pending && !editing && (
                          <button
                            data-testid={`coach-reinvite-${c.user_id}`}
                            onClick={() => handleReinvite(c)}
                            className="px-3 py-2 border border-slate-200 text-xs text-slate-700 hover:bg-slate-100 transition-colors flex items-center gap-1"
                            title="Einladungslink anzeigen / kopieren"
                          >
                            <Link2 size={12} strokeWidth={1.5} /> Einladungslink
                          </button>
                        )}
                        {canDelete(c) && !editing && (
                          <button
                            data-testid={`coach-delete-${c.user_id}`}
                            onClick={() => handleDelete(c)}
                            className="px-3 py-2 border border-slate-200 text-xs text-red-600 hover:bg-red-50 hover:border-red-300 transition-colors flex items-center gap-1"
                          >
                            <Trash2 size={12} strokeWidth={1.5} /> Löschen
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {isAdmin && (
        <>
          <AddCoachDialog
            open={addOpen}
            onOpenChange={setAddOpen}
            defaultRole={isLecturersMode ? "lecturer" : isStaffMode ? "manager" : "coach"}
            lockRole={isLecturersMode}
            onCreated={(data) => setCoaches((arr) => [...arr, { ...data, invitation_pending: true }])}
          />
          <InviteLinkDialog
            open={!!inviteFor}
            onOpenChange={(o) => { if (!o) setInviteFor(null); }}
            coach={inviteFor}
          />
        </>
      )}
    </div>
  );
}
